import type { UserRole } from '../types/onboarding.types';
import usStatesData from './USStates.json';
import metroRegionsData from './zoomInfoMetroRegions.json';

// Step lists based on user role
export const BASE_STEP_LIST = [
  'Company',
  'Locations',
  'Services',
  'Size',
  'Industries',
  'Job Titles',
  'Email Setting',
  'Integrations',
];

export const ADMIN_STEP_LIST = [...BASE_STEP_LIST, 'Invite'];

// Questions and helper text
export const QUESTIONS_MAP = {
  Company: 'What company do you work at?',
  Locations: 'What geographic locations do you serve?',
  Services: 'What services do you offer?',
  Size: 'What is the company size of your ideal customer?',
  Industries: 'What industries are your customers in?',
  'Job Titles': 'What job titles you would like to target?',
  'Email Setting': 'Set up your email signature and communication style',
  Integrations: 'Connect your accounts',
  Invite: 'Invite users to your Commander team',
} as const;

export const HELPER_TEXT_MAP = {
  Company: 'Fill in the text boxes below',
  Locations: 'Select or add all that apply',
  Services: 'Select or add all that apply',
  Size: 'Select all that apply',
  Industries: 'Select all that apply',
  'Job Titles': 'Select or add all that apply',
  'Email Setting': 'Customize how you appear in email communications',
  Integrations: 'Select accounts',
  Invite: 'Input user email',
} as const;

// Suggestions for each step (matching original onboarding)
export const SUGGESTIONS_MAP = {
  Locations: ['Los Angeles,California', 'Arcadia, California', '90034', '95041', 'Germany'],
  Services: ['Commercial waste', 'Recycling', 'Roll-off', 'Dumpsters'],
  Size: ['1-5', '5-30', '30+'],
  Industries: ['Construction', 'Manufacturing', 'Medical', 'Restaurants', 'Hotels'],
  'Job Titles': ['Owner', 'Operations', 'CTO', 'Director'],
} as const;

// Communication styles data (matching original onboarding)
export const COMMUNICATION_STYLES = [
  {
    style: 'Default',
    description: [
      'Commander will generate an email based on proven success rates.',
      'i.e. intentional misspellings, grammatical errors so that we sound human',
    ],
    example: {
      greeting: 'Hey John,',
      paragraphs: [
        "Saw you're keeping WI's dairy crown shining with valley's new facility. Nice work!",
        "FWIW – we've helped 3 food producers in your area cut waste costs by ~30% by turning byproducts into resellable stuff.No strings, but I'd handle the cost analysis if useful.",
        'Free for a 15-min call to brainstorm?',
      ],
      closing: 'Best regards, James',
    },
  },
  {
    style: 'Casual',
    description: [
      'A straightforward email that maintains a professional tone, with a clear call to action.',
    ],
    example: {
      greeting: 'Hi John,',
      paragraphs: [
        "I noticed Green Valley's work in dairy ingredients – we've helped similar Wisconsin food producers cut waste costs by recycling packaging/pallet materials.",
        'Any interest in a 15-minute chat on how we might streamline your logistics? No pitch, just ideas.',
      ],
      closing: 'Thanks, James',
    },
  },
  {
    style: 'Formal',
    description: ['A more formal, longer email with professional language and detailed context.'],
    example: {
      greeting: 'Hi John,',
      paragraphs: [
        "I'm James from CommanderAI. We partner with food manufacturers to convert packaging and production waste into cost-saving resources—without compromising NSF/Kosher compliance.",
        "Given Green Valley's focus on operational precision, I'd love to explore how we've helped similar facilities reduce disposal costs by up to 30% through tailored recycling programs.",
        'Could we schedule a 15-minute call this week?',
      ],
      closing: 'Sincerely, James',
    },
  },
  {
    style: 'Other',
    description: ["If you'd like a specific email modeled, enter it below."],
  },
] as const;

// Location data (imported from JSON files)
// Transform US states data
const USA_STATES_DATA = usStatesData.map((state) => ({
  name: state.name,
  type: 'STATE',
}));

// Transform metro regions data
const METRO_REGIONS_DATA = metroRegionsData.map((region) => ({
  name: region.Name,
  type: 'METRO_REGION',
}));

// Combined location data
export const USA_STATES = [
  ...USA_STATES_DATA,
  ...METRO_REGIONS_DATA,
  { name: 'United States', type: 'COUNTRY' },
  { name: 'Canada', type: 'COUNTRY' },
] as const;

// Integration options
export const INTEGRATION_OPTIONS = [
  {
    id: 'hubspot',
    description: 'Connect your HubSpot CRM',
    icon: 'hubspot',
  },
  {
    id: 'salesforce',
    description: 'Sync with your Salesforce CRM',
    icon: 'salesforce',
  },
  {
    id: 'linkedin',
    description: 'Connect your LinkedIn account for professional networking',
    icon: 'linkedin',
  },
  {
    id: 'calendly',
    description: 'Connect your Calendly account for scheduling',
    icon: 'calendly',
  },
  {
    id: 'microsoftDynamics',
    description: 'Connect your Microsoft Dynamics account for CRM',
    icon: 'microsoftDynamics',
  },
  {
    id: 'twentyCRM',
    description: 'Connect your Twenty CRM account for scheduling',
    icon: 'twentyCRM',
  },
  {
    id: 'pipedrive',
    description: 'Connect your Pipedrive account for sales management',
    icon: 'pipedrive',
  },
  {
    id: 'zohoCRM',
    description: 'Connect your Zoho CRM account for scheduling',
    icon: 'zohoCRM',
  },
] as const;

// Integration action configurations — re-exported from shared module
export { INTEGRATION_ACTIONS } from '../../integrations/integrations.constants';

// Validation constants
export const DOMAIN_REGEX =
  /^(https?:\/\/)?(localhost(:[0-9]{1,5})?(\/.*)?)$|^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/.*)?$/;

export const EMAIL_REGEX =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

// Colors and styling constants (matching design system)
export const TAG_STYLES = 'bg-primary-600 text-white rounded-l-[4px] px-3 py-1 flex items-center';

export const BACKGROUND_STYLES = {
  // Matching the original onboarding background pattern
  gradient:
    'linear-gradient(99deg, rgba(195, 219, 217, 0.5) 7.5%, rgba(206, 225, 234, 0.5) 47.67%, rgba(122, 187, 218, 0.5) 97.39%)',
} as const;

// Step completion validation rules
export const getStepListForRole = (role: UserRole | null): string[] => {
  if (role === 'admin') {
    return ADMIN_STEP_LIST;
  }
  return BASE_STEP_LIST;
};

// Location filtering function
export const filterLocationSuggestions = (
  input: string,
  selectedLocations: string[] = []
): Array<{ name: string; type: string }> => {
  if (!input.trim()) {
    return USA_STATES.filter((state) => !selectedLocations.includes(state.name));
  }

  const inputLower = input.toLowerCase();
  return USA_STATES.filter(
    (state) =>
      state.name.toLowerCase().includes(inputLower) && !selectedLocations.includes(state.name)
  );
};

// Maximum limits
export const MAX_INVITE_USERS = 4;

// Local storage keys
export const STORAGE_KEYS = {
  FORM_DATA: 'onboarding_form_data',
  INPUT_STATES: 'onboarding_input_states',
  PROGRESS: 'onboarding_progress',
  USER_DETAILS: 'user_details',
} as const;

// Error messages
export const ERROR_MESSAGES = {
  COMPANY_NAME_REQUIRED: 'Please enter your company name',
  DOMAIN_REQUIRED: 'Please enter your domain',
  DOMAIN_INVALID: 'Please enter a valid domain (e.g., example.com)',
  LOCATION_REQUIRED: 'Please add at least one location',
  SERVICE_REQUIRED: 'Please add at least one service',
  SIZE_REQUIRED: 'Please select at least one company size',
  INDUSTRY_REQUIRED: 'Please add at least one industry',
  JOB_TITLE_REQUIRED: 'Please add at least one job title',
  EMAIL_SIGNATURE_REQUIRED: 'Please enter your email signature',
  COMMUNICATION_STYLE_TEXT_REQUIRED:
    'Please provide a sample email for the custom communication style',
  INVITE_USER_REQUIRED: 'Please add at least one user to invite',
  INVALID_EMAIL: 'Please enter a valid email address',
  MAX_INVITE_USERS: 'You can only invite up to 4 users',
  EMAIL_EXISTS: 'This email has already been added',
  NAME_REQUIRED: 'Please enter both name and email',
} as const;
